import { bboxIntersects, type BBox, type Stroke } from 'shared';

const CELL = 100; // unidades lógicas

/**
 * Grade uniforme sobre a página. Sem ela, apagar com a borracha vira uma
 * varredura de 3.000 traços por evento de caneta — e o evento tem 4 ms.
 */
export class SpatialIndex {
  private cells = new Map<string, Set<string>>();
  private strokes = new Map<string, Stroke>();

  private *keysFor(b: BBox): Generator<string> {
    const x0 = Math.floor(b[0] / CELL);
    const y0 = Math.floor(b[1] / CELL);
    const x1 = Math.floor(b[2] / CELL);
    const y1 = Math.floor(b[3] / CELL);
    for (let x = x0; x <= x1; x++) for (let y = y0; y <= y1; y++) yield `${x}:${y}`;
  }

  add(s: Stroke): void {
    this.strokes.set(s.id, s);
    for (const key of this.keysFor(s.bbox)) {
      let set = this.cells.get(key);
      if (!set) this.cells.set(key, (set = new Set()));
      set.add(s.id);
    }
  }

  remove(id: string): void {
    const s = this.strokes.get(id);
    if (!s) return;
    for (const key of this.keysFor(s.bbox)) this.cells.get(key)?.delete(id);
    this.strokes.delete(id);
  }

  clear(): void {
    this.cells.clear();
    this.strokes.clear();
  }

  get(id: string): Stroke | undefined {
    return this.strokes.get(id);
  }

  all(): Stroke[] {
    return [...this.strokes.values()].sort((a, b) => a.createdAt - b.createdAt || (a.id < b.id ? -1 : 1));
  }

  get size(): number {
    return this.strokes.size;
  }

  /** Candidatos cujo bbox toca a área — ainda precisam de teste fino. */
  query(area: BBox, pad = 0): Stroke[] {
    const ids = new Set<string>();
    for (const key of this.keysFor([area[0] - pad, area[1] - pad, area[2] + pad, area[3] + pad])) {
      const set = this.cells.get(key);
      if (set) for (const id of set) ids.add(id);
    }
    const out: Stroke[] = [];
    for (const id of ids) {
      const s = this.strokes.get(id);
      if (s && bboxIntersects(s.bbox, area, pad)) out.push(s);
    }
    return out.sort((a, b) => a.createdAt - b.createdAt);
  }
}
